import { useContext, useEffect, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import Select from "react-select";
import { ARAgingAsOfDateReportAPI } from "./services/ReportServices";
import "./style/common.scss";
import BlueSeedLogo from "../img/BlueSeed-logo-blue.png";
import backArrow from "img/back-arrow.svg";

import {
  dateFormmaterNoTime,
  fetchReportParamsQuery,
  formatNumber,
} from "global/helpers";
import AgGrid from "elements/AgGrid";
import { useReactToPrint } from "react-to-print";
import DispatchContext from "context/DispatchContext";

import { useHistory, useLocation } from "react-router-dom";

import exportIcon from "img/export.svg";

import Filter from "./components/Filter";

export default function ARAgingAsOfDate() {
  const [currentElement, setCurrentElement] = useState(1);
  const [, setRefreshCounter] = useState(0);
  const appDispatch = useContext(DispatchContext);
  const history = useHistory();
  const childFnRef = useRef(null);
  const childFnRef1 = useRef(null);
  const childFnRef2 = useRef(null);

  const { search } = useLocation();

  const queryParams = fetchReportParamsQuery(search);

  const customizedComponentId = queryParams[1];

  const isCustomizedReport = customizedComponentId != null;

  const methods = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { handleSubmit, control, getValues } = methods;

  const [reportData, setReportData] = useState(null);

  const generateReport = useMutation(ARAgingAsOfDateReportAPI, {
    async onSuccess(data) {
      let currentData = { ...data };
      setReportData(currentData);
      setCurrentElement(getValues("groupingElement")?.value ?? 1);
    },
  });

  const onSubmit = () => {
    const values = getValues();
    let obj = {
      groupingElement: values.groupingElement?.value,
      customerId: values.CustomerId?.value,
      hubId: values.HubId?.value,
      marketId: values.MarketId?.value,
      repId: values.RepresentativeId?.value,
      regionId: values.RegionId?.value,
      invoiceHub: values.IsInvoiceHub?.value,
      period: values.groupingPeriod?.value,
    };
    generateReport.mutate(obj);
  };

  const [isSummary, setIsSummary] = useState(true);

  const handlePrint = useReactToPrint({
    content: () => reportPage.current,
  });

  const printDocument = () => {
    appDispatch({ type: "loading", value: true });
    setTimeout(function () {
      handlePrint();
    }, 1000);
    setTimeout(function () {
      appDispatch({ type: "loading", value: false });
    }, 5000);
  };

  const onBackToReportsClick = () => {
    history.push("/reports");
  };

  useEffect(() => {
    appDispatch({ type: "loading", value: generateReport.isLoading });
  }, [appDispatch, generateReport.isLoading]);

  const reportPage = useRef(null);

  const [exportToExcelClicked, setExportToExcelClicked] =
    useState<boolean>(false);
  const [disabledExportBtn, setDisabledExportBtn] = useState<boolean>(false);
  const [isLoadingGridExport, setIsLoadingGridExport] =
    useState<boolean>(false);

  const exportToExcel = () => {
    setIsLoadingGridExport(true);
    setDisabledExportBtn(true);
    setExportToExcelClicked(true);
    let notification = {
      variant: "success",
      msg: "Preparing grid for export...",
    };
    appDispatch({ type: "notification", value: notification });
  };

  useEffect(() => {
    appDispatch({ type: "loading", value: isLoadingGridExport });
  }, [appDispatch, isLoadingGridExport]);

  const groupingElementsOpt = [
    {
      value: 1,
      label: "None",
    },
    {
      value: 2,
      label: "Hubs",
    },
    {
      value: 3,
      label: "Customers",
    },
    {
      value: 4,
      label: "Period Of Aging",
    },
  ];

  const isSummaryOpt = [
    {
      value: true,
      label: "Summary",
    },
    {
      value: false,
      label: "Detailed",
    },
  ];

  useEffect(() => {
    setReportData(null);
    onSubmit();
  }, [isSummary]);

  useEffect(() => {
    let params = {
      invoiceHub: true,
      groupingElement: 1,
    };
    if (!isCustomizedReport) generateReport.mutate(params);
  }, [isCustomizedReport]);

  const headers = ["No Grouping", "Customers", "Header 3"];

  const columns = [
    [
      {
        field: "elementName",
        headerName: reportData?.groupTitle ?? "",
        cellRenderer: "agGroupCellRenderer",
        pinned: "left",
        lockPostion: true,
        suppressMenu: true,
        floatingFilter: false,
        filter: false,
        suppressMovable: true,
      },
      {
        field: "total_Amount",
        headerName: "Total QTY",
      },
      {
        field: "total_OpenBalance",
        headerName: "Open Balance",
        valueGetter: (params) => {
          return formatNumber(params?.data?.total_OpenBalance);
        },
      },
      {
        field: "total_PaidAmount",
        headerName: "Paid Amount",
        valueGetter: (params) => {
          return formatNumber(params?.data?.total_PaidAmount);
        },
      },
    ],
    [
      {
        field: "accountNumber",
        headerName: "Account No",
        cellRenderer: "agGroupCellRenderer",
        pinned: "left",
        lockPostion: true,
        suppressMenu: true,
        floatingFilter: false,
        filter: false,
        suppressMovable: true,
      },
      {
        field: "balance",
        headerName: "Balance",
        valueGetter: (params) => {
          return formatNumber(params.data.balance);
        },
      },
      {
        field: "total_Amount",
        headerName: "Total QTY",
        filter: false,
        floatingFilter: false,
        editable: false,
        resizable: false,
        sortable: false,
        suppressMenu: true,
      },
      {
        field: "total_OpenBalance",
        headerName: "Open Balance",
        valueGetter: (params) => {
          return formatNumber(params?.data?.total_OpenBalance);
        },
        filter: false,
        floatingFilter: false,
        editable: false,
        resizable: false,
        sortable: false,
        suppressMenu: true,
      },
      {
        field: "total_PaidAmount",
        headerName: "Paid Amount",
        valueGetter: (params) => {
          return formatNumber(params?.data?.total_PaidAmount);
        },
        filter: false,
        floatingFilter: false,
        editable: false,
        resizable: false,
        sortable: false,
        suppressMenu: true,
      },
    ],
    [
      {
        field: "hubKey",
        headerName: "Hub",
        hide: currentElement == 2,
      },
      {
        field: "invoiceNumber",
        headerName: "Invoice No",
      },
      {
        field: "age",
        headerName: "Age",
      },
      {
        field: "invoiceAmount",
        headerName: "Inv Amount",
        valueGetter: (params) => {
          return formatNumber(params.data.invoiceAmount);
        },
      },
      {
        field: "invoiceDate",
        headerName: "Inv Date",
        valueGetter: (params) => {
          return dateFormmaterNoTime(params.data.invoiceDate);
        },
      },
      {
        field: "balance",
        headerName: "Balance",
        valueGetter: (params) => {
          return formatNumber(params.data.balance);
        },
      },
      {
        field: "openBalance",
        headerName: "Open Balance",
        valueGetter: (params) => {
          return formatNumber(params.data.openBalance);
        },
      },
      {
        field: "paidAmount",
        headerName: "Paid Amount",
        valueGetter: (params) => {
          return formatNumber(params.data.paidAmount);
        },
      },
      {
        field: "lastPayment",
        headerName: "Last Payment",
        valueGetter: (params) => {
          return formatNumber(params.data.lastPayment);
        },
      },
      {
        field: "lastPayDate",
        headerName: "Last Payment Date",
        valueGetter: (params) => {
          return dateFormmaterNoTime(params.data.lastPayDate);
        },
      },
    ],
  ];
  const detailCellRenderer = ({ data }) => {
    return (
      <div className="py-4">
        <AgGrid
          data={data?.invoices}
          columns={gridColumns[2]}
          pagination={false}
          autoHight={true}
          ref={childFnRef2}
          autoSize={true}
        />
      </div>
    );
  };

  const detailCellRenderer2 = ({ data }) => {
    return (
      <div className="py-4">
        <AgGrid
          data={data?.customers}
          columns={gridColumns[1]}
          pagination={false}
          autoHight={true}
          masterDetail={true}
          detailCellRenderer={detailCellRenderer}
          subTable={gridColumns[2]}
          subTableObj={"invoices"}
          ref={childFnRef1}
        />
      </div>
    );
  };

  const [gridColumns, setGridColumns] = useState(
    columns.map((column) => {
      return column.map((c) => ({ ...c, hide: false }));
    })
  );

  const [reportName, setReportName] = useState("AR Aging as of date");

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="page-title page-title-editable">
            <div className="back-btn " onClick={onBackToReportsClick}>
              <img src={backArrow} alt="back arrow" />
              {reportName}
            </div>
          </div>
          <div className="page-content-wrapper">
            <div className={`page-content ${isSummary ? "remove-arrow" : ""}`}>
              <Filter
                filters={[0, 25, 2, 4, 6, 26, 19, 20]}
                onSubmit={onSubmit}
                gridColumns={gridColumns}
                setGridColumns={setGridColumns}
                setReportName={setReportName}
                headers={headers}
                groupingElements={groupingElementsOpt}
                isSummary={isSummary}
                setIsSummary={setIsSummary}
                childFnRef={childFnRef.current}
                childFnRef1={childFnRef1.current}
                childFnRef2={childFnRef2.current}
                setRefreshCounter={setRefreshCounter}
              />
              <div className="container report-filters-div mb-3">
                <div className="d-flex justify-content-between">
                  <div className="col-2">
                    {reportData?.groups[0]?.customers != null && (
                      <>
                        <Controller
                          control={control}
                          name="isSummary"
                          render={({ field: { onChange, value } }) => (
                            <Select
                              options={isSummaryOpt}
                              isSearchable={true}
                              value={isSummaryOpt?.find(
                                (opt) => opt?.value === isSummary
                              )}
                              onChange={(val) => setIsSummary(val?.value)}
                            />
                          )}
                        />
                      </>
                    )}
                  </div>
                  <div>
                    {currentElement === 1 && (
                      <button
                        className="import-btn"
                        type="button"
                        onClick={exportToExcel}
                        disabled={
                          disabledExportBtn || reportData?.hubs?.length === 0
                        }
                      >
                        <img src={exportIcon} className="me-2" alt="" />
                        Export grid to Excel
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-success ms-5"
                      onClick={printDocument}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-printer me-3"
                        viewBox="0 0 16 16"
                      >
                        <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z" />
                        <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z" />
                      </svg>
                      Print report to PDF
                    </button>
                  </div>
                </div>
              </div>
              <div className="report-page" ref={reportPage}>
                <div className="report-header d-flex justify-content-between">
                  <div>
                    <h3>
                      <b>
                        {reportName}{" "}
                        {reportData?.groupTitle === "Nothing"
                          ? ""
                          : `grouped by ${reportData?.groupTitle}`}
                      </b>
                    </h3>{" "}
                    <br />
                    <p>
                      <b>Report Date: </b>
                      {dateFormmaterNoTime(reportData?.reportDate)}
                    </p>
                  </div>
                  <div>
                    <img
                      alt=""
                      src={reportData?.imageLogo ?? BlueSeedLogo}
                      className="report-logo"
                    ></img>
                  </div>
                </div>
                <div className="grid-wrapper">
                  {currentElement === 1 && (
                    <>
                      <AgGrid
                        data={reportData?.groups[0]?.invoices}
                        columns={gridColumns[2]}
                        pagination={false}
                        forPrinting={true}
                        autoHight={true}
                        masterDetail={false}
                        exportToExcel={exportToExcelClicked}
                        setExportToExcelClicked={setExportToExcelClicked}
                        setDisabledExportBtn={setDisabledExportBtn}
                        setIsLoadingGridExport={setIsLoadingGridExport}
                        fileName={reportName}
                        ref={childFnRef}
                        isReport={isCustomizedReport}
                        autoSize={true}
                      />
                      <div className="mt-3 pb-3">
                        <div className="d-flex justify-content-between">
                          <div className="label">Total</div>
                          <div className="value">
                            Amount:{" "}
                            {formatNumber(reportData?.groups[0]?.total_Amount)}
                          </div>
                          <div className="value">
                            Paid:{" "}
                            {formatNumber(
                              reportData?.groups[0]?.total_PaidAmount
                            )}
                          </div>
                          <div className="value">
                            Balance:{" "}
                            {formatNumber(
                              reportData?.groups[0]?.total_OpenBalance
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {currentElement === 2 && (
                    <>
                      <AgGrid
                        data={reportData?.groups}
                        columns={gridColumns[0]}
                        pagination={false}
                        forPrinting={true}
                        autoHight={true}
                        exportToExcel={exportToExcelClicked}
                        setExportToExcelClicked={setExportToExcelClicked}
                        setDisabledExportBtn={setDisabledExportBtn}
                        setIsLoadingGridExport={setIsLoadingGridExport}
                        masterDetail={true}
                        detailCellRenderer={detailCellRenderer2}
                        subTable={gridColumns[1]}
                        subTableObj={"customers"}
                        fileName={reportName}
                        ref={childFnRef}
                        isReport={isCustomizedReport}
                      />
                    </>
                  )}
                  {currentElement === 3 && (
                    <>
                      <AgGrid
                        data={reportData?.groups}
                        columns={gridColumns[0]}
                        pagination={false}
                        forPrinting={true}
                        autoHight={true}
                        masterDetail={true}
                        detailCellRenderer={detailCellRenderer}
                        exportToExcel={exportToExcelClicked}
                        setExportToExcelClicked={setExportToExcelClicked}
                        setDisabledExportBtn={setDisabledExportBtn}
                        setIsLoadingGridExport={setIsLoadingGridExport}
                        subTable={gridColumns[2]}
                        subTableObj={"invoices"}
                        fileName={reportName}
                        ref={childFnRef}
                        isReport={isCustomizedReport}
                      />
                      <div className="mt-3 pb-3">
                        <div className="d-flex justify-content-between">
                          <div className="label">Total</div>
                          <div className="value">
                            Amount: {formatNumber(reportData?.total_Amount)}
                          </div>
                          <div className="value">
                            Paid: {formatNumber(reportData?.total_PaidAmount)}
                          </div>
                          <div className="value">
                            Balance:{" "}
                            {formatNumber(reportData?.total_OpenBalance)}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {currentElement === 4 && (
                    <>
                      <AgGrid
                        data={reportData?.groups}
                        columns={gridColumns[0]}
                        pagination={false}
                        forPrinting={true}
                        autoHight={true}
                        masterDetail={true}
                        detailCellRenderer={detailCellRenderer}
                        exportToExcel={exportToExcelClicked}
                        setExportToExcelClicked={setExportToExcelClicked}
                        setDisabledExportBtn={setDisabledExportBtn}
                        setIsLoadingGridExport={setIsLoadingGridExport}
                        subTable={gridColumns[2]}
                        subTableObj={"invoices"}
                        fileName={reportName}
                        ref={childFnRef}
                        isReport={isCustomizedReport}
                      />
                      <div className="mt-3 pb-3">
                        <div className="d-flex justify-content-between">
                          <div className="label">Total</div>
                          <div className="value">
                            Amount: {formatNumber(reportData?.total_Amount)}
                          </div>
                          <div className="value">
                            Paid: {formatNumber(reportData?.total_PaidAmount)}
                          </div>
                          <div className="value">
                            Balance:{" "}
                            {formatNumber(reportData?.total_OpenBalance)}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
}
